// The consent banner and the "Your privacy choices" dialog (every page). Essential storage is
// the saved choice itself; the optional categories are analytics — PostHog (usage measurement
// and session replay, inputs masked) and Google Analytics (page views) — and advertising — the Reddit Pixel (ad conversion
// measurement), each of which web/site/site.ts starts only when this says so.
//
// Two consent models, picked per visitor by the server from the IP's country (server/geo.ts,
// the `featurette-consent` meta): opt-in (EU/EEA, UK, Switzerland, Canada, and anywhere the
// server can't place) shows the banner and runs nothing optional until accepted, its dialog's
// boxes unticked; opt-out (everywhere else) shows no banner and runs both until the visitor
// turns them off from the footer's "Your privacy choices" ([data-cookie-settings]). Global
// Privacy Control always turns advertising off, whatever was saved, and the page says so
// ([data-gpc-honored], the dialog) as California's rules ask. A saved choice wins over either
// model's default; one saved before advertising existed reads as advertising off. The choice
// lives in localStorage; /cookies shows the current state in its [data-cookie-summary] line.

const KEY = "featurette-cookie-preferences-v2";

interface Allow {
  analytics: boolean;
  advertising: boolean;
}

interface Choice extends Allow {
  savedAt: string;
}

function stored(): Choice | null {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<Choice> | null;
    return raw && typeof raw.analytics === "boolean"
      ? {
          analytics: raw.analytics,
          advertising: raw.advertising === true,
          savedAt: raw.savedAt ?? "",
        }
      : null;
  } catch {
    return null;
  }
}

/** Mounts the banner + dialog. `analytics` runs once, as soon as analytics is allowed (PostHog
 * cannot be stopped mid-page); `advertising` / `noAdvertising` run on every load and save, by
 * whether advertising is allowed — a withdrawal stops the pixel and a re-accept restarts it. */
export function cookieConsent(on: {
  analytics: () => void;
  advertising: () => void;
  noAdvertising: () => void;
}): void {
  const optOut =
    document.querySelector('meta[name="featurette-consent"]')?.getAttribute("content") ===
    "opt-out";
  const gpc =
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  let choice = stored();
  /** What runs now: the saved choice, else the model's default; GPC keeps advertising off. */
  const allowed = (): Allow => {
    const base = choice ?? { analytics: optOut, advertising: optOut };
    return { analytics: base.analytics, advertising: base.advertising && !gpc };
  };
  let analyticsStarted = false;
  const apply = () => {
    const now = allowed();
    if (now.analytics && !analyticsStarted) {
      analyticsStarted = true;
      on.analytics();
    }
    if (now.advertising) on.advertising();
    else on.noAdvertising();
  };

  const banner = document.createElement("section");
  banner.className = "cookie-banner";
  banner.setAttribute("aria-label", "Cookie choices");
  banner.innerHTML =
    "<h2>Your cookie choices.</h2>" +
    "<p>Essential storage keeps the site working and remembers this choice. Optional " +
    "analytics helps us see how the site is used — page views and session replays with " +
    "everything you type masked. Optional advertising lets Reddit tell us whether our ads " +
    "on Reddit bring visitors here. Both run only if you accept.</p>" +
    '<div class="cookie-actions">' +
    '<button type="button" class="primary" data-cookie-choice="accept">Accept optional</button>' +
    '<button type="button" data-cookie-choice="reject">Reject optional</button>' +
    '<button type="button" data-cookie-choice="customize">Customize</button>' +
    "</div>";

  const dialog = document.createElement("dialog");
  dialog.className = "cookie-dialog";
  dialog.setAttribute("aria-labelledby", "cookieDialogTitle");
  dialog.innerHTML =
    '<div class="cookie-dialog-inner">' +
    '<button type="button" class="close" aria-label="Close privacy choices">×</button>' +
    '<h2 id="cookieDialogTitle">Your privacy choices.</h2>' +
    "<p>Choose which optional categories may run. Essential storage is always on.</p>" +
    '<div class="cookie-row"><div><strong>Essential</strong><small>Site operation and your saved choice. Always active.</small></div><span class="always-on">Always on</span></div>' +
    '<label class="cookie-row"><span><strong>Analytics</strong><small>PostHog and Google Analytics: page views, clicks and session replays with inputs masked, so we can see what works.</small></span><input id="cookieAnalytics" type="checkbox"></label>' +
    '<label class="cookie-row"><span><strong>Advertising</strong><small>Reddit Pixel: tells Reddit you visited, so we can measure whether our Reddit ads work. Turning this off opts you out of the sale or sharing of your personal information for advertising.</small>' +
    '<small class="gpc-honored" data-gpc-note hidden>Opt-out request honored: your browser sends Global Privacy Control, so advertising stays off.</small></span><input id="cookieAdvertising" type="checkbox"></label>' +
    '<div class="cookie-dialog-actions">' +
    '<button type="button" data-cookie-choice="reject">Reject optional</button>' +
    '<button type="button" class="primary" data-cookie-choice="save">Save choices</button>' +
    '<button type="button" data-cookie-choice="accept">Accept optional</button>' +
    "</div></div>";
  document.body.append(banner, dialog);

  const analytics = dialog.querySelector<HTMLInputElement>("#cookieAnalytics");
  const advertising = dialog.querySelector<HTMLInputElement>("#cookieAdvertising");
  const summary = document.querySelector<HTMLElement>("[data-cookie-summary]");
  if (gpc) {
    dialog.querySelector<HTMLElement>("[data-gpc-note]")?.removeAttribute("hidden");
    if (advertising) advertising.disabled = true;
    for (const note of document.querySelectorAll<HTMLElement>("[data-gpc-honored]")) {
      note.hidden = false;
    }
  }
  const updateSummary = () => {
    if (!summary) return;
    const now = allowed();
    const state = `analytics ${now.analytics ? "on" : "off"}, advertising ${now.advertising ? "on" : "off"}`;
    summary.textContent =
      (choice ? `Saved choice: ${state}.` : `No choice saved yet: ${state} by default.`) +
      (gpc ? " Opt-out request honored (Global Privacy Control)." : "");
  };
  const save = (allow: Allow) => {
    choice = { ...allow, savedAt: new Date().toISOString() };
    try {
      localStorage.setItem(KEY, JSON.stringify(choice));
    } catch {
      // private mode / storage off: the choice holds for this page only
    }
    banner.hidden = true;
    if (dialog.open) dialog.close();
    updateSummary();
    apply();
    document.dispatchEvent(new CustomEvent("featurette:cookie-preferences", { detail: choice }));
  };
  const open = () => {
    // The boxes show what runs now: unticked before any choice under opt-in (consent is never
    // pre-ticked there), ticked under opt-out; advertising unticked under GPC.
    const now = allowed();
    if (analytics) analytics.checked = now.analytics;
    if (advertising) advertising.checked = now.advertising;
    banner.hidden = true;
    if (!dialog.open) dialog.showModal();
  };

  banner.hidden = optOut || choice !== null;
  updateSummary();
  apply();

  document.addEventListener("click", (e) => {
    const trigger = (e.target as Element | null)?.closest<HTMLElement>(
      "[data-cookie-choice],[data-cookie-settings]",
    );
    if (!trigger) return;
    if (trigger.matches("[data-cookie-settings]")) {
      e.preventDefault();
      open();
      return;
    }
    switch (trigger.dataset.cookieChoice) {
      case "accept":
        save({ analytics: true, advertising: true });
        break;
      case "reject":
        save({ analytics: false, advertising: false });
        break;
      case "customize":
        open();
        break;
      case "save":
        save({
          analytics: analytics?.checked ?? false,
          // Under GPC the box is disabled and unticked; it shows the signal, not a choice, so
          // the advertising choice stays what it was (or the model's default).
          advertising: advertising?.disabled
            ? (choice?.advertising ?? optOut)
            : (advertising?.checked ?? false),
        });
        break;
    }
  });
  dialog.querySelector(".close")?.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close();
  });
  dialog.addEventListener("close", () => {
    if (!choice && !optOut) banner.hidden = false;
  });
}
